import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

/**
 * A password field with an eye to see what was typed.
 *
 * Revealed, it is a plain text input, so capitalising and spellcheck are off the
 * whole time: a phone must not "correct" a password, or send it to a spellchecker.
 */
const PasswordInput = ({ label, ...input }) => {
  const id = useId();
  const field = useRef(null);
  const caret = useRef(null);
  const [shown, setShown] = useState(false);

  // Changing an input's type can lose its selection, and Safari moves the caret to
  // the end, which is the wrong place to be when checking a typo halfway along
  useLayoutEffect(() => {
    const saved = caret.current;
    caret.current = null;
    if (saved && document.activeElement === field.current) field.current.setSelectionRange(saved[0], saved[1]);
  }, [shown]);

  // Covered up again on submit, so a failed attempt doesn't leave it on screen
  useEffect(() => {
    const form = field.current?.form;
    if (!form) return undefined;
    const hide = () => setShown(false);
    form.addEventListener('submit', hide);
    return () => form.removeEventListener('submit', hide);
  }, []);

  const toggle = () => {
    caret.current = [field.current.selectionStart, field.current.selectionEnd];
    setShown((now) => !now);
  };

  return (
    <div className="login-field">
      <label className="login-k" htmlFor={id}>
        {label}
      </label>
      <div className="pw">
        <input
          {...input}
          ref={field}
          id={id}
          type={shown ? 'text' : 'password'}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck="false"
        />
        <button
          type="button"
          className="pw-eye"
          aria-label={shown ? 'Hide password' : 'Show password'}
          aria-pressed={shown}
          // Keeps a pointer press from taking focus out of the field mid-typing
          onMouseDown={(event) => event.preventDefault()}
          onClick={toggle}
        >
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
            <circle cx="12" cy="12" r="3" />
            {shown && <path d="M4 4l16 16" />}
          </svg>
        </button>
      </div>
    </div>
  );
};

export default PasswordInput;
